<template>
    <span :class="eLabelClasses">
        <slot></slot>
    </span>
</template>

<script>
export default {
    name: 'DkELabel',
    props: {
        /**
         * Sets width to be auto or fixed (based on efficiency class)
         */
        widthAuto: {
            type: Boolean,
            default: false,
        },
        /**
         * Sets color based on efficiency class (see dropdown)
         */
        eClass: {
            type: String,
            default: 'a',
            validator(value) {
                return ['a+++', 'a++', 'a+', 'a', 'b', 'c', 'd', 'e', 'f', 'g'].includes(value.toLowerCase());
            },
        },
        /**
         * Sets side of the arrow
         */
        arrow: {
            type: String,
            default: 'right',
            validator(value) {
                return ['left', 'right', 'both'].includes(value);
            },
        },
        /**
         * Sets size of the label
         */
        size: {
            type: String,
            default: 'default',
            validator(value) {
                return ['default', 'small', 'large'].includes(value);
            },
        },
        /**
         * Sets background: dark (black) or based on efficiency class
         */
        isDark: {
            type: Boolean,
            default: false,
        },
        /*
         * Flag to apply custom width.
         */
        isVehicleCard: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isVisible: true,
        };
    },
    computed: {
        eClassParsed() {
            return this.eClass.toLowerCase();
        },
        eLabelClasses() {
            const labelClasses = [
                'dk-e-label',
                'tw-relative',
                'tw-inline-flex',
                'tw-items-center',
                'tw-text-white',
                'tw-font-bold',
                { 'tw-text-sm tw-px-2 tw-h-6': this.size === 'default' },
                { 'dk-e-label--sm tw-text-xs tw-px-1 tw-h-4': this.size === 'small' },
                { 'dk-e-label--lg tw-text-xl tw-px-2 tw-h-8': this.size === 'large' },
                { 'has--arrow-left': this.arrow === 'left' },
                { 'has--arrow-right': this.arrow === 'right' },
                { 'has--arrow-left has--arrow-right': this.arrow === 'both' },
                {
                    'tw-bg-[#008F36] before:tw-border-r-[#008F36] after:tw-border-l-[#008F36]':
                        !this.isDark && ['a+++', 'a++', 'a+', 'a'].includes(this.eClassParsed),
                },
                {
                    'tw-bg-[#33AE00] before:tw-border-r-[#33AE00] after:tw-border-l-[#33AE00]':
                        !this.isDark && this.eClassParsed === 'b',
                },
                {
                    'tw-bg-[#C7D300] before:tw-border-r-[#C7D300] after:tw-border-l-[#C7D300]':
                        !this.isDark && this.eClassParsed === 'c',
                },
                {
                    'tw-bg-[#F8E501] before:tw-border-r-[#F8E501] after:tw-border-l-[#F8E501]':
                        !this.isDark && this.eClassParsed === 'd',
                },
                {
                    'tw-bg-[#FFB700] before:tw-border-r-[#FFB700] after:tw-border-l-[#FFB700]':
                        !this.isDark && this.eClassParsed === 'e',
                },
                {
                    'tw-bg-[#FC5F00] before:tw-border-r-[#FC5F00] after:tw-border-l-[#FC5F00]':
                        !this.isDark && this.eClassParsed === 'f',
                },
                {
                    'tw-bg-[#F40000] before:tw-border-r-[#F40000] after:tw-border-l-[#F40000]':
                        !this.isDark && this.eClassParsed === 'g',
                },
                { 'tw-bg-slate-900 before:tw-border-r-slate-900 after:tw-border-l-slate-900': this.isDark },
            ];

            if (this.widthAuto) {
                return labelClasses;
            }

            if (this.size === 'default' && !this.isVehicleCard) {
                labelClasses.push(
                    { 'tw-min-w-[44px]': this.eClassParsed === 'a+++' },
                    { 'tw-min-w-[56px]': this.eClassParsed === 'a++' },
                    { 'tw-min-w-[66px]': this.eClassParsed === 'a+' },
                    { 'tw-min-w-[80px]': this.eClassParsed === 'a' },
                    { 'tw-min-w-[92px]': this.eClassParsed === 'b' },
                    { 'tw-min-w-[105px]': this.eClassParsed === 'c' },
                    { 'tw-min-w-[116px]': this.eClassParsed === 'd' },
                    { 'tw-min-w-[126px]': this.eClassParsed === 'e' },
                    { 'tw-min-w-[140px]': this.eClassParsed === 'f' },
                    { 'tw-min-w-[153px]': this.eClassParsed === 'g' }
                );
            }

            if (this.size === 'small' && !this.isVehicleCard) {
                labelClasses.push(
                    { 'tw-min-w-[35px]': this.eClassParsed === 'a+++' },
                    { 'tw-min-w-[40px]': this.eClassParsed === 'a++' },
                    { 'tw-min-w-[45px]': this.eClassParsed === 'a+' },
                    { 'tw-min-w-[55px]': this.eClassParsed === 'a' },
                    { 'tw-min-w-[60px]': this.eClassParsed === 'b' },
                    { 'tw-min-w-[68px]': this.eClassParsed === 'c' },
                    { 'tw-min-w-[78px]': this.eClassParsed === 'd' },
                    { 'tw-min-w-[86px]': this.eClassParsed === 'e' },
                    { 'tw-min-w-[92px]': this.eClassParsed === 'f' },
                    { 'tw-min-w-[101px]': this.eClassParsed === 'g' }
                );
            }

            if (this.size === 'large' && !this.isVehicleCard) {
                labelClasses.push(
                    { 'tw-min-w-[60px]': this.eClassParsed === 'a+++' },
                    { 'tw-min-w-[72px]': this.eClassParsed === 'a++' },
                    { 'tw-min-w-[88px]': this.eClassParsed === 'a+' },
                    { 'tw-min-w-[105px]': this.eClassParsed === 'a' },
                    { 'tw-min-w-[120px]': this.eClassParsed === 'b' },
                    { 'tw-min-w-[135px]': this.eClassParsed === 'c' },
                    { 'tw-min-w-[155px]': this.eClassParsed === 'd' },
                    { 'tw-min-w-[170px]': this.eClassParsed === 'e' },
                    { 'tw-min-w-[185px]': this.eClassParsed === 'f' },
                    { 'tw-min-w-[200px]': this.eClassParsed === 'g' }
                );
            }

            if (this.isVehicleCard) {
                labelClasses.push('tw-min-w-[32px]');
            }

            return labelClasses;
        },
    },
};
</script>

<style>
.dk-e-label.has--arrow-left:before {
    @apply tw-absolute tw-top-0 tw-right-full tw-border-[12px] tw-border-l-transparent tw-border-t-transparent tw-border-b-transparent;
    content: '';
}

.dk-e-label.has--arrow-right:after {
    @apply tw-absolute tw-top-0 tw-left-full tw-border-[12px] tw-border-r-transparent tw-border-t-transparent tw-border-b-transparent;
    content: '';
}

.dk-e-label--sm.has--arrow-left:before {
    @apply tw-border-[8px];
}

.dk-e-label--sm.has--arrow-right:after {
    @apply tw-border-[8px];
}

.dk-e-label--lg.has--arrow-left:before,
.dk-e-label--lg.has--arrow-right:after {
    @apply tw-border-[16px];
}

.dk-e-label--lg.dk-e-label--blunt.has--arrow-left:before,
.dk-e-label--lg.dk-e-label--blunt.has--arrow-right:after {
    border-left-width: 12px;
    border-right-width: 12px;
}
</style>
